import React from "react";
import { motion } from "framer-motion";
import { Card, CardContent, Box, styled, Typography } from "@mui/material";

const CARD_OFFSET = 10;
const SCALE_FACTOR = 0;

const Wrapper = styled(Box)({
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  width: "100%",
});

const CardWrap = styled(Box)({
  paddingLeft: "0",
  position: "relative",
  width: "402px",
  height: "269px",
});

const cardStyle: React.CSSProperties = {
  position: "absolute",
  width: "402px",
  height: "269px",
  borderRadius: "12px",
  transformOrigin: "top center",
  listStyle: "none",
  boxShadow: "0 4px 24px 0 rgba(0,0,0,0.12)",
};

export interface TestimonialData {
  children: React.ReactNode;
}

export interface CardStackProps {
  children: React.ReactNode;
}

const StyledCard = styled(Card)({
  display: "flex",
  borderRadius: "12px",
  backgroundColor: "#0A100E",
  width: "100%",
  height: "100%",
  alignItems: "center",
  justifyContent: "center",
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "12px",
    padding: "1px",
    background:
      "linear-gradient(180deg, rgba(250, 250, 250, 0.2) 0%, rgba(250, 250, 250, 0.05) 16%, rgba(250, 250, 250, 0.05) 86%, rgba(255, 255, 255, 0) 100%)",
    mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    WebkitMask:
      "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    maskComposite: "exclude",
    WebkitMaskComposite: "destination-out",
    pointerEvents: "none",
  },
});

export const CardText = styled(Typography)({
  fontSize: "21px",
  fontWeight: "300",
  lineHeight: "140%",
});

export const CardTitle = styled(Typography)({
  fontSize: "21px",
  fontWeight: "450",
  lineHeight: "140%",
  textAlign: "center",
});

const arrayMove = <T,>(arr: T[], from: number, to: number): T[] => {
  const newArr = arr.slice();
  const [moved] = newArr.splice(from, 1);
  newArr.splice(to, 0, moved);
  return newArr;
};

export const CardStack: React.FC<CardStackProps> = ({ children }) => {
  const childrenWithKeys = React.useMemo(() => {
    const childrenArray = React.Children.toArray(children);
    return childrenArray.map((child, i) => ({
      key: `card-${i}`,
      content: child,
    }));
  }, [children]);

  const [cards, setCards] = React.useState(() => {
    const shuffled = [...childrenWithKeys];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  });
  const [autoDragging, setAutoDragging] = React.useState(false);
  const [dragY, setDragY] = React.useState(0);

  const intervalRef = React.useRef<NodeJS.Timeout | null>(null);

  const moveToEnd = React.useCallback(
    (from: number) => {
      setCards(arrayMove(cards, from, cards.length - 1));
    },
    [cards],
  );

  React.useEffect(() => {
    if (cards.length <= 1) return;
    intervalRef.current = setInterval(() => {
      setAutoDragging(true);
      setDragY(-200);
      setTimeout(() => {
        moveToEnd(0);
        setDragY(0);
        setAutoDragging(false);
      }, 300);
    }, 20 * 1000); // Rotate every 20 seconds
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [cards, moveToEnd]);

  const handleDragStart = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setAutoDragging(false);
  };

  return (
    <Wrapper>
      <CardWrap>
        {cards?.map((card, index) => {
          const canDrag = index === 0;
          return (
            <motion.li
              key={card.key}
              style={{
                ...cardStyle,
                cursor: canDrag ? "grab" : "auto",
                height: "100%",
              }}
              animate={{
                top: index * -CARD_OFFSET,
                scale: 1 - index * SCALE_FACTOR,
                zIndex: (cards?.length || 0) - index,
                y: canDrag && autoDragging ? dragY : 0,
              }}
              transition={{ y: { duration: 0.5 } }}
              drag={canDrag ? "y" : false}
              dragConstraints={{
                top: 0,
                bottom: 0,
              }}
              onDragStart={canDrag ? handleDragStart : undefined}
              onDragEnd={() => moveToEnd(index)}
            >
              {card.content}
            </motion.li>
          );
        })}
      </CardWrap>
    </Wrapper>
  );
};

export const TesimonialCard: React.FC<TestimonialData> = ({ children }) => {
  return (
    <StyledCard>
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "24px",
          padding: "24px",
          "&:last-child": {
            paddingBottom: "24px",
          },
        }}
      >
        {children}
      </CardContent>
    </StyledCard>
  );
};
